/**
 * `countsTowardDelivery(item)` / `deliveryCountableItems(items)` /
 * `countDelivery(items)` — THE client's answer to *"is this line something
 * anybody receives?"*, and the only correct way to count a delivery on the
 * client.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚖️ THE OWNER'S RULING, 2026-09-19
 * ═══════════════════════════════════════════════════════════════════════════
 * **An adjustment line does NOT count toward delivery at all.** A hire price
 * adjustment (`order_items.adjusts_item_id` non-NULL, minted by the off-hire
 * dialog) is a FINANCIAL line, not goods. A fully-received order stays
 * `Delivered`; nobody is ever asked to tick a credit note. His end state:
 *
 *     ORD-042  Delivered   3 of 3 received      (true AND quiet)
 *
 * over today's `Delivered, 3 of 4` (a quiet lie) and over `Partial delivery,
 * 3 of 4` (honest, but it churns a finished order back onto the queue).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔑 IT IS ONE MODULE BECAUSE SEVEN SURFACES ASK THE SAME QUESTION
 * ═══════════════════════════════════════════════════════════════════════════
 * `DepartmentOrders.js` and `MyOrders.js` carried a BYTE-IDENTICAL five-line
 * block (`receivedCount` + `allItemsReceived`); `OrderExpandedRow.js`,
 * `OrderPage.js` (twice), `ApprovalStatus.js`, `deliveryTooltip.js`,
 * `deliveredTickNotice.js` and `orderCompletionPrompt.js` each had their own.
 * The house rule is to expand a good idea across the site as SHARED code,
 * never by copying — and the server half of this same ruling had to extract
 * two byte-identical `delivery_info` blocks for exactly the reason this exists:
 * *the copies agreed by luck, having been written together.*
 *
 * ⚠️ **SIX OTHER SURFACES ARE NOT IN THAT LIST AND MUST NOT BE "FIXED" HERE.**
 * The six step sublabels in `utils/orderStepInfo.js`, the table's progress bar
 * in `OrderTableView.js`, and `OrderForm.js`'s section title all read
 * `order.delivery_info` or a `received` prop — they are CONSUMERS of a count
 * the SERVER already filters (`helpers/deliveryInfo.js`). Adding a filter
 * there would be a second source of truth for a number that arrives correct.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 THE TWO MARKERS, AND WHY THIS ACCEPTS EITHER
 * ═══════════════════════════════════════════════════════════════════════════
 * The client meets order items in two shapes and they carry DIFFERENT markers,
 * because one of the two crosses a security boundary:
 *
 *   - **Detail rows** (`GET /api/orders/:id`, `SELECT oi.*`) carry the raw
 *     `adjusts_item_id`.
 *   - **Preview rows** (`preview_items` on the three LIST endpoints) carry a
 *     derived `is_adjustment` BOOLEAN and deliberately not the id.
 *     `ORDER_PREVIEW_ITEM_FIELDS` is a frozen allowlist whose rule is *never
 *     add an id "because it is only an id"* — it carries no `id` at all, and
 *     forwarding this one would disclose the shape of the order's adjustment
 *     graph (which line corrects which) through the one UNGATED array on those
 *     endpoints.
 *
 * 🪤 `is_adjustment` is checked FIRST. A row carrying both is a preview row
 * that someone has merged detail fields into; the derived flag is the one the
 * server computed, so it wins.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚨 THIS DOES NOT THROW, AND THE SERVER'S TWIN DOES. THE ASYMMETRY IS
 *    DELIBERATE — WRITE IT DOWN RATHER THAN "FIXING" IT.
 * ═══════════════════════════════════════════════════════════════════════════
 * `server/helpers/deliveryStatus.js#countsTowardDelivery` THROWS a TypeError
 * on a row carrying NEITHER marker. It must: `undefined == null` is `true`, so
 * a silent miss there writes a wrong status INTO THE DATABASE, where it
 * persists and is read by everyone.
 *
 * Here the failure modes are not symmetric, so the code is not either:
 *   - A throw in a render path WHITE-SCREENS THE PAGE. The cost of a strict
 *     contract is paid by the user, on a surface where the wrong answer is one
 *     stale render that the next refresh corrects.
 *   - A response already in flight across a deploy carries no marker THROUGH
 *     NO FAULT OF THE CLIENT. The client cannot fix a payload it did not build.
 *
 * So an absent marker counts as GOODS — today's pre-ruling behaviour, i.e. the
 * worst case is the count this surface showed yesterday.
 *
 * 🔒 **WHAT MAKES THAT SAFE RATHER THAN LAZY IS A RATCHET, AND IT IS
 * LOAD-BEARING, NOT A NICETY.** §2c of `server/tests/api/order-preview-items
 * .test.js` asserts mechanically that EVERY projection feeding a counting
 * surface carries the marker — the three `preview_items` SELECTs, the four
 * recompute SELECTs, the restore clamp and the `delivery_info` aggregate. An
 * absent marker therefore cannot ship; this fallback exists for the seconds
 * during a deploy when an old response is still in a browser, and for nothing
 * else. If you delete that ratchet, this lenience becomes a silent miscount
 * and the asymmetry stops being defensible.
 */

/**
 * @param {object} item an order item — a detail row or a `preview_items` row.
 * @returns {boolean} whether the line is GOODS (counts toward delivery).
 */
export function countsTowardDelivery(item) {
  if (!item) return true;
  // The derived flag the server computed, when present. `=== true` is wrong
  // here only if the server ever sends `1`; it sends a real boolean, and a
  // truthy test would also swallow a stray non-empty string.
  if (typeof item.is_adjustment === 'boolean') return !item.is_adjustment;
  // 🪤 `!= null`, NOT truthiness — `0` is not a reachable row id on either
  // engine today, but a truthy test would reclassify an adjustment row as
  // goods if it ever were, and this value decides a delivery count.
  if ('adjusts_item_id' in item) return item.adjusts_item_id == null;
  // Neither marker. Counts as goods — see the asymmetry note above.
  return true;
}

/** The goods rows of a list, in order. Never throws; a non-array reads empty. */
export function deliveryCountableItems(items) {
  return (Array.isArray(items) ? items : []).filter(countsTowardDelivery);
}

/**
 * `{ received, total }` over the GOODS rows — the pair every "N of M received"
 * surface renders.
 *
 * 🪤 `Boolean(item.is_received)`, NEVER `=== true`. `order_items.is_received`
 * is `INTEGER DEFAULT 0` on BOTH engines and arrives as `1`/`0`, so a strict
 * check counts NOTHING — the trap `server/helpers/deliveryStatus.js` records
 * at length. Contrast `has_delivery_paperwork`, where a missing value IS a
 * real third answer and `=== true` is required: same word, opposite rule.
 */
export function countDelivery(items) {
  const countable = deliveryCountableItems(items);
  return {
    received: countable.filter((i) => i && Boolean(i.is_received)).length,
    // 🔀 PER-LINE "ORDERED" (2026-10-28): a received line counts as ordered
    // too — the server enforces is_received ⇒ is_ordered, and a legacy row
    // that predates the column must not read "delivered 1, ordered 0".
    ordered: countable.filter((i) => i && (Boolean(i.is_ordered) || Boolean(i.is_received))).length,
    total: countable.length,
  };
}

/**
 * Every GOODS row received — and there is at least one.
 *
 * 🪤 **THE `total > 0` TERM IS LOAD-BEARING and is the client's copy of the
 * server's `totalItems > 0` guard.** Without it `0 === 0` reads as "everything
 * arrived" for an order with no goods at all, which is how an empty order
 * renders as fully delivered. ⚠️ Note this is NOT the same question
 * `constants/orderCompletionPrompt.js#allItemsReceived` asks — that one
 * deliberately answers `true` for an empty list, because "nothing outstanding
 * to warn about" is its meaning. Two questions, two answers; do not unify them.
 */
export function allGoodsReceived(items) {
  const { received, total } = countDelivery(items);
  return total > 0 && received === total;
}
